import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useSelectedMVComment, useAssetMVPermissions } from '@hooks';
import type { fabric } from 'fabric';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import { FabricContext } from '@context/FabricContext';
import CommentItem from '@pages/MediaViewer/Comments/components/CommentItem';
import CommentEditor from '@pages/MediaViewer/Comments/components/CommentEditor';
import type { SubmitEditorValues } from '@pages/MediaViewer/Comments/components/CommentEditor/CommentEditor';
import { Comment } from 'antd';
import {
  AssetItemDto,
  AssetVersionCommentDto,
  AssetVersionItemDto
} from '@api/Api';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import {
  MediaViewerOutletContextState,
  UpdateAssetCommentParams
} from '@layouts/types';
import { useAuth } from '@hooks/useAuth';
import { nonNull } from '@helpers/non-null';
import { useCreateTask } from '@hooks/tasks';
import useTypedDispatch from '@hooks/useTypedDispatch';
import { openTaskModal } from '@redux/reducers/tasks';
import { buildCommentActions } from '@pages/MediaViewer/Comments/buildCommentActions';
import { useMVCommentAttachUppy } from '@hooks/useMVCommentAttachUppy';
import {
  buildEditCommentPayload,
  createTurnCommentIntoTask
} from '@pages/MediaViewer/helpers/comment';
import { useCommentMentions } from '@pages/MediaViewer/hooks/useComment';
import getObjectViewportPosition, {
  choosePosition,
  constrainPositionToWindow,
  getObjectViewportRect
} from '@pages/MediaViewer/AssetViewer/Canvas/utils/getObjectViewportPosition';

import './FloatCommentItem.less';

type FloatCommentItemProps = {
  asset: AssetItemDto;
  selectedVersion: AssetVersionItemDto;
  commentMentions: boolean;
  canCreateTask: boolean;
  openCommentsPanel?: () => void;
};

function FloatCommentItem({
  asset,
  selectedVersion,
  commentMentions,
  canCreateTask,
  openCommentsPanel
}: FloatCommentItemProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    removeComment,
    setCommentResolve,
    setCommentLike,
    editComment,
    getPotentialMentions,
    getPotentialGroupMentions
  } = useOutletContext<MediaViewerOutletContextState>();
  const { user } = useAuth();
  const dispatch = useTypedDispatch();
  const createTask = useCreateTask();
  const canLeaveComment = useAssetMVPermissions('comment');
  const canLikeComment = useAssetMVPermissions('likeComments');
  const { canvas } = useContext(FabricContext);
  const selectedComment = useSelectedMVComment();
  const {
    selectedCommentId,
    setCanvasMode,
    setDocumentHighlight,
    selectComment,
    setDrawToolsShape,
    isHideComments
  } = useAssetViewerState();

  const [position, setPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const [editCommentId, setEditCommentId] = useState<string | null>(null);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState<boolean>(true);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const uppy = useMVCommentAttachUppy(asset.id || '');
  const { potentialMentions, potentialGroupMentions, setSearchQuery } =
    useCommentMentions({
      assetVersionId: selectedVersion?.id,
      getPotentialMentions,
      getPotentialGroupMentions
    });

  const isValidSelected =
    !!selectedCommentId &&
    selectedCommentId !== 'new' &&
    selectedCommentId !== 'reply';

  const updatePosition = useCallback(() => {
    if (!canvas || !isValidSelected || !selectedComment?.id) return;
    const canvasObjects = canvas
      .getObjects()
      .filter((o) => o.type !== 'highlight' && o.visible !== false);
    const activeObject = canvas.getActiveObject();
    const anchorObject =
      activeObject &&
      activeObject.type !== 'highlight' &&
      (activeObject as any).visible !== false
        ? activeObject
        : canvasObjects[0];

    if (anchorObject) {
      const initialPos = getObjectViewportPosition(canvas, anchorObject as any);
      const constrained = constrainPositionToWindow(initialPos);
      const objectRect = getObjectViewportRect(canvas, anchorObject as any);
      const finalPos =
        objectRect && constrained
          ? choosePosition(canvas, objectRect, constrained) ?? constrained
          : constrained;
      setPosition(finalPos);
      return;
    }

    const highlight = selectedComment.documentHighlight;
    const rects = selectedComment.documentHighlightRects;
    if (!highlight || !rects?.length) {
      setPosition(null);
      return;
    }
    const canvasEl = canvas.getElement();
    if (!canvasEl) {
      setPosition(null);
      return;
    }
    const canvasRect = canvasEl.getBoundingClientRect();
    const lastRect = rects[rects.length - 1];
    const GAP = 8;
    const left = canvasRect.left + (lastRect.left / 100) * canvasRect.width;
    const top =
      canvasRect.top +
      ((lastRect.top + lastRect.height) / 100) * canvasRect.height +
      GAP;
    setPosition(constrainPositionToWindow({ left, top }));
  }, [canvas, isValidSelected, selectedComment]);

  useEffect(() => {
    updatePosition();
  }, [updatePosition]);

  useEffect(() => {
    if (!canvas) return;
    canvas.on('after:render', updatePosition);
    canvas.on('object:modified', updatePosition);
    canvas.on('object:moving', updatePosition);
    canvas.on('selection:updated', updatePosition);
    canvas.on('selection:created', updatePosition);
    canvas.on('selection:cleared', updatePosition);
    return () => {
      canvas.off('after:render', updatePosition);
      canvas.off('object:modified', updatePosition);
      canvas.off('object:moving', updatePosition);
      canvas.off('selection:updated', updatePosition);
      canvas.off('selection:created', updatePosition);
      canvas.off('selection:cleared', updatePosition);
    };
  }, [canvas, updatePosition]);

  const onClick = useCallback(() => {
    if (!selectedComment) return;
    selectComment(selectedComment.id);
    setCanvasMode('view');
    setDocumentHighlight(
      selectedComment.documentHighlight
        ? {
            text: selectedComment.documentHighlight,
            rects: selectedComment.documentHighlightRects
          }
        : null
    );
  }, [selectComment, selectedComment, setCanvasMode, setDocumentHighlight]);

  const onResetAsset = useCallback(() => {
    selectComment(null);
    setCanvasMode('view');
    setDocumentHighlight(null);
    setEditCommentId(null);
    setDrawToolsShape('cursor');
  }, [
    selectComment,
    setCanvasMode,
    setDocumentHighlight,
    setEditCommentId,
    setDrawToolsShape
  ]);

  const onDeleteComment = useCallback(
    async (id: string) => {
      await removeComment(id);
      onResetAsset();
    },
    [removeComment, onResetAsset]
  );

  const onResolveClick = useCallback(
    async (comment: AssetVersionCommentDto) => {
      if (!user) return;
      const {
        id: userId,
        type,
        pictureUrl,
        email,
        firstName,
        lastName
      } = nonNull(user);
      await setCommentResolve(
        { id: comment.id, value: !comment.resolvedAt } as any,
        {
          id: userId,
          type: type === 'internal' ? 'user' : 'external-reviewer',
          picture: { url: pictureUrl ?? null },
          email,
          firstName,
          lastName: lastName || '',
          me: true
        }
      );
    },
    [setCommentResolve, user]
  );

  const onLikeClick = useCallback(async () => {
    if (!selectedComment || !user) return;
    const hasLike = selectedComment.likes.find((el: any) => el.me);
    const {
      id: userId,
      type,
      pictureUrl,
      email,
      firstName,
      lastName
    } = nonNull(user);
    await setCommentLike({ id: selectedComment.id, value: !hasLike } as any, {
      id: userId,
      type: type === 'internal' ? 'user' : 'external-reviewer',
      picture: { url: pictureUrl ?? null },
      email,
      firstName,
      lastName: lastName || '',
      me: true
    });
  }, [selectedComment, setCommentLike, user]);

  const onTurnCommentIntoTask = createTurnCommentIntoTask({
    asset,
    user,
    createTask,
    dispatch
  });

  const getAnnotations = useCallback((): string | null => {
    if (!canvas) return null;
    const json = canvas.toJSON(['assetAngle', 'userEmail', 'userId']) as any;
    const objects = (json.objects || []).filter(
      (item: fabric.Object) => item.type !== 'highlight'
    );
    if (!objects.length) return null;
    return JSON.stringify(objects);
  }, [canvas]);

  const onEditComment = useCallback(
    async ({
      text,
      rawText,
      mentions,
      groupMentions,
      idOrParentId: id,
      documentHighlight,
      documentHighlightRects,
      documentHighlightReplacement
    }: SubmitEditorValues) => {
      if (!selectedComment) return;
      await editComment({
        ...buildEditCommentPayload(
          {
            text,
            rawText,
            mentions,
            groupMentions,
            idOrParentId: id,
            documentHighlight,
            documentHighlightRects,
            documentHighlightReplacement
          },
          selectedComment
        ),
        annotations: getAnnotations()
      } as UpdateAssetCommentParams);
      onResetAsset();
    },
    [editComment, getAnnotations, selectedComment, onResetAsset]
  );

  const shouldShow = useMemo(
    () => isValidSelected && !!selectedComment?.id && !!position,
    [isValidSelected, position, selectedComment?.id]
  );

  useEffect(() => {
    if (!shouldShow) return;
    if (editCommentId) return;
    const clearSelectedComment = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (containerRef.current?.contains(target)) return;
      if (target.closest('.media_viewer_menu')) return;
      onResetAsset();
    };
    document.addEventListener('mousedown', clearSelectedComment);
    return () =>
      document.removeEventListener('mousedown', clearSelectedComment);
  }, [shouldShow, editCommentId]);

  const clampToViewport = useCallback((pos: { left: number; top: number }) => {
    const el = containerRef.current;
    if (!el) return pos;
    const width = el.offsetWidth || 0;
    const height = el.offsetHeight || 0;
    const PADDING = 8;
    const maxLeft = window.innerWidth - width - PADDING;
    const maxTop = window.innerHeight - height - PADDING;
    return {
      left: Math.max(PADDING, Math.min(pos.left, maxLeft)),
      top: Math.max(PADDING, Math.min(pos.top, maxTop))
    };
  }, []);

  useLayoutEffect(() => {
    if (!position) return;
    const clamped = clampToViewport(position);
    if (clamped.left !== position.left || clamped.top !== position.top) {
      setPosition(clamped);
    }
  }, [position, clampToViewport]);

  useEffect(() => {
    if (!position) return;
    const onResize = () => {
      setPosition((prev) => (prev ? clampToViewport(prev) : prev));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [position, clampToViewport]);

  if (isHideComments) return null;
  if (!shouldShow || !selectedComment) return null;

  const actions = buildCommentActions({
    comment: selectedComment as any,
    taskId: selectedComment.task?.id || null,
    canLeaveComment,
    canLikeComment,
    onSelectComment: onClick,
    onOpenTask: () => {
      if (!selectedComment.task?.id) return;
      dispatch(
        openTaskModal({
          taskId: selectedComment.task.id,
          descriptionPlaceholder:
            'Describe your task so your teammates know what to do.'
        })
      );
    },
    onReply: () => {
      const replyToId = selectedComment.topParentId || selectedComment.id;
      const params = new URLSearchParams(searchParams);
      params.set('commentId', selectedComment.id);
      params.set('replyToCommentId', replyToId);
      setSearchParams(params);
      openCommentsPanel?.();
    },
    onLikeClick
  });

  return (
    <div
      ref={containerRef}
      className="float_comment_item"
      style={{ left: position!.left, top: position!.top }}
    >
      <Comment
        actions={actions}
        content={
          editCommentId === selectedComment.id ? (
            <CommentEditor
              commentMentions={commentMentions}
              comment={selectedComment as any}
              editorFor="edit"
              editCommentId={selectedComment.id}
              setEditCommentId={setEditCommentId}
              replyToCommentId={null}
              setReplyToCommentId={() => undefined}
              isFocused={true}
              onSubmitEditor={onEditComment}
              isCheckboxChecked={isCheckboxChecked}
              setIsCheckboxChecked={setIsCheckboxChecked}
              checkboxValueType={null}
              potentialMentions={potentialMentions}
              potentialGroupMentions={potentialGroupMentions}
              searchPotentialMentions={(v) => setSearchQuery(v)}
              uppy={uppy}
            />
          ) : (
            <CommentItem
              comment={selectedComment}
              setEditCommentId={(id) => setEditCommentId(id)}
              setReplyToCommentId={(replyToCommentId) => {
                if (!replyToCommentId) return;
                const replyToId =
                  selectedComment.topParentId ||
                  replyToCommentId ||
                  selectedComment.id;
                const params = new URLSearchParams(searchParams);
                params.set('commentId', selectedComment.id);
                params.set('replyToCommentId', replyToId);
                setSearchParams(params);
                openCommentsPanel?.();
              }}
              onDeleteComment={onDeleteComment}
              onTurnCommentIntoTask={onTurnCommentIntoTask}
              onResolveClick={onResolveClick}
              onClickComment={onClick}
              canCreateTask={canCreateTask}
            />
          )
        }
      />
    </div>
  );
}

export default FloatCommentItem;
